import Vue from 'vue'
import clone from 'lodash/clone'
import find from 'lodash/find'
import { mapState, mapGetters } from 'vuex'
import Platform from '@/plugin-platforms'
// import toPairs from 'lodash/toPairs'
// import { mapState } from 'vuex'
import { webUrl } from '@/lib/route'
import { IconFormats, IllustrationFormats, PhotoFormats, FillTypes, PreviewSizes } from '@/constants'
import AuthMixin from '@/components/mixins/auth'

export default {
  mixins: [AuthMixin],

  data () {
    return {
      IconFormats,
      IllustrationFormats,
      PhotoFormats
    }
  },

  computed: {
    ...mapState({
      asset: state => state.settings.asset
    }),
    ...mapGetters({
      canInsert: 'insert/canInsert',
      insertAsSvg: 'insert/insertAsSvg'
    }),
    // eslint-disable-next-line
/////////////////////////
//////////////////
///////////////////////////////////////////////
/////////////////////////////////////////////////////////
//////
///////////////////////////////
//////////
    vectorSupported () {
      return this.isLoggedIn && Platform.features.vector
    },
    fillTypeSupported () {
      return Platform.features.fillType
    },
    previewSizes () {
      return PreviewSizes.map(size => {
        return {
          text: this.$t(`pages.settings.plugin.previewSizes.${size}`),
          value: size
        }
      })
    },
    fillTypes () {
      return FillTypes.map(format => {
        return {
          text: this.$t(`pages.settings.plugin.fillTypes.${format}`),
          value: format
        }
      })
    },
    importDnd: {
      get () {
        if (this.item) {
          return this.$store.state.settings.importDND[this.item.payable_price ? 'premium' : 'free'][this.asset]
        } else {
          return this.$store.state.settings.importDND.free[this.asset]
        }
      },
      set (value) {
        if (this.item) {
          this.$store.commit('settings/setImportDND', {
            asset: this.asset,
            price: this.item.payable_price ? 'premium' : 'free',
            value
          })
        } else {
          this.$store.commit('settings/setImportDND', {
            asset: this.asset,
            price: 'free',
            value
          })
        }
      }
    },
    importDndPremium: {
      get () {
        return this.$store.state.settings.importDND.premium[this.asset]
      },
      set (value) {
        this.$store.commit('settings/setImportDND', {
          asset: this.asset,
          price: 'premium',
          value
        })
      }
    },
    selectedSize: {
      get () {
        return find(this.previewSizes, { value: this.$store.state.settings.previewSize })
      },
      set (value) {
        this.$store.commit('settings/setPreviewSize', value.value)
      }
    },
    selectedIconFormat: {
      get () {
        return this.$store.state.settings.iconFormat
      },
      set (value) {
        this.$store.commit('settings/setIconFormat', value)
      }
    },
    selectedIllustrationFormat: {
      get () {
        return this.$store.state.settings.illustrationFormat
      },
      set (value) {
        this.$store.commit('settings/setIllustrationFormat', value)
      }
    },
    selectedPhotoFormat: {
      get () {
        return this.$store.state.settings.photoFormat
      },
      set (value) {
        this.$store.commit('settings/setPhotoFormat', value)
      }
    },
    selectedFillType: {
      get () {
        return find(this.fillTypes, { value: this.$store.state.settings.fillType })
      },
      set (value) {
        this.$store.commit('settings/setFillType', value.value)
      }
    },
    // Item Related Actions
    isPremium () {
      return this.item && this.item.payable_price > 0
    },
    downloadedByUser () {
      return this.item && this.item.licenses.find(license => license.downloaded_by_user === true)
    },
    canUserDownload () {
      return this.item && this.item.licenses[0].is_subscription
    },
    isLocked () {
      if (this.item) {
        return !this.isLoggedIn || !(this.downloadedByUser || this.canUserDownload)
      } else {
        return !this.isLoggedIn
      }
    },
    isCompOnly () {
      return !this.isLoggedIn || (this.isPremium && !this.canUserDownload)
    },
    hasIllustrationSVG () {
      return this.asset === 'illustration' && this.item && this.item.formats && this.item.formats.find(f => f.name === 'svg')
    },
    availableFormats () {
      if (this.asset === 'icon') {
        // Messages
        // Please login to insert SVG icon in your design.
        // PNG of 512 px will be inserted in your design or selected shape.

        // Subscribe Iconscout to insert SVG of premium icons
        // Watermarked PNG of 512 px will be inserted in your design or selected shape.

        // Press Command + V to insert the vector icon in your design.
        // PNG of 512 px will be inserted in your design or selected shape.

        // Subscribe Iconscout to insert SVG of premium icon.
        // PNG of 512 px will be inserted in your design or selected shape.

        // Press Command + V to insert the vector icon in your design.
        // PNG of 512 px will be inserted in your design or selected shape.

        const formats = []

        // If Vector is supported
        if (Platform.features.vector) {
          formats.push({
            format: 'svg',
            text: this.$t(`pages.settings.plugin.iconFormats.svg`),
            isLocked: this.isLocked,
            isComp: false,
            description: this.isLoggedIn ? (this.isLocked ? 'Subscribe Iconscout to insert SVG of premium icons' : (process.env.VUE_APP_PLATFORM === 'xd' ? `Press ${this.isWindows ? 'Ctrl' : 'Command'} + V to insert the vector icon in your design.` : 'SVG will be inserted in your design or selected shape.')) : 'Please login to insert SVG icon in your design.'
          })
        }

        formats.push({
          format: 'png',
          text: this.$t(`pages.settings.plugin.iconFormats.png`),
          isLocked: false,
          isComp: this.isCompOnly,
          description: (this.isCompOnly && this.item && this.item.payable_price > 0) ? 'Watermarked PNG of 512 px will be inserted in your design or selected shape.' : 'PNG of 512 px will be inserted in your design or selected shape.'
        })

        return formats
      } else if (this.asset === 'illustration') {
        const formats = [
          {
            format: 'thumb',
            text: this.$t(`pages.settings.plugin.illustrationFormats.thumb`),
            isLocked: this.isLocked,
            isComp: false,
            description: this.isLoggedIn ? (this.isLocked ? 'Subscribe Iconscout to insert Thumbnail of premium illustration' : `PNG will be inserted in your design or selected shape.`) : 'Please login to insert Thumbnail illustration in your design.'
          },
          {
            format: 'png',
            text: this.$t(`pages.settings.plugin.illustrationFormats.png`),
            isLocked: false,
            isComp: this.isCompOnly,
            description: (this.isCompOnly && this.item && this.item.payable_price > 0) ? 'Watermarked PNG will be inserted in your design or selected shape.' : 'PNG will be inserted in your design or selected shape.'
          }
        ]

        // If Vector is supported
        if (Platform.features.vector && (this.hasIllustrationSVG || !this.item)) {
          formats.push({
            format: 'svg',
            text: this.$t(`pages.settings.plugin.illustrationFormats.svg`),
            isLocked: this.isLocked,
            isComp: false,
            description: this.isLoggedIn ? (this.isLocked ? 'Subscribe Iconscout to insert SVG of premium illustrations' : (process.env.VUE_APP_PLATFORM === 'xd' ? `Press ${this.isWindows ? 'Ctrl' : 'Command'} + V to insert the vector icon in your design.` : 'SVG will be inserted in your design or selected shape.')) : 'Please login to insert SVG illustration in your design.'
          })
        }
        return formats
      } else if (this.asset === 'photo') {
        return [
          {
            format: 'thumb',
            text: this.$t(`pages.settings.plugin.photoFormats.thumb`),
            isLocked: false,
            isComp: this.isCompOnly,
            description: (this.isCompOnly && this.item && this.item.payable_price > 0) ? 'Watermarked Thumbnail will be inserted in your design or selected shape.' : 'Thumbnail will be inserted in your design or selected shape.'
          },
          {
            format: 'original',
            text: this.$t(`pages.settings.plugin.photoFormats.original`),
            isLocked: this.isLocked,
            isComp: false,
            description: this.isLoggedIn ? (this.isLocked ? 'Subscribe Iconscout to insert Thumbnail of premium photo' : `JPG will be inserted in your design or selected shape.`) : 'Please login to insert Original photo in your design.'
          }
        ]
      }
    },
    selectedFormat: {
      get () {
        const selected = this.getSelectedFormat(this.asset)
        return this.availableFormats.find(f => f.format === selected)
      },
      set (format) {
        if (this.asset === 'icon') {
          this.selectedIconFormat = format.format
        } else if (this.asset === 'illustration') {
          this.selectedIllustrationFormat = format.format
        } else if (this.asset === 'photo') {
          this.selectedPhotoFormat = format.format
        }
      }
    },
    showPremiumOptions () {
      return this.canUserDownload && this.isPremium && !this.downloadedByUser
    },
    premiumOption: {
      get () {
        return this.$store.state.settings.premiumOption
      },
      set (value) {
        this.$store.commit('settings/setPremiumOption', value)
      }
    }
  },

  methods: {
    getSelectedFormat (asset) {
      switch (asset) {
        case 'icon':
          return this.selectedIconFormat
        case 'illustration':
          return this.selectedIllustrationFormat
        case 'photo':
          return this.selectedPhotoFormat
      }
    },
    getSelectedFormatText (asset) {
      return this.$t(`pages.settings.plugin.${asset}Formats.${this.getSelectedFormat(asset)}`)
    },
    async showImportFormat (asset, item = null) {
      // eslint-disable-next-line indent
/////////////////////////
      // eslint-disable-next-line indent
      this.$store.dispatch('modal/show', {
        name: 'modalImportFormat'
      })
      // eslint-disable-next-line indent
/////////
///////////////////////////////////////////////////////

///////////////////////////////////////
////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////

/////////////////////////////////////////////////////
///////////////////////////////////////////////////////////
///////////////////////////////////////

/////////////////////////////////
///////////////////////////
///////////////////////////////
/////////////////////////////////////
//////////////////
//////////////
/////////////
////////////////////
////////////////////////////////////////////////////////////////////
/////////
////////

//////////////////////////////////////////
///////////////////////////

///////////////////////////
/////////////////////////
/////////////////////////////////////////////
///////////////
/////////////////////
/////////////////////////////
/////////////////////////
////////////
//////////////////////////////////////////////
///////////////
//////////////////////////////
//////////////////////////////////////////////
///////////////
///////
////////////////
////////////////////////////////////////
//////////
    }
  }
}
